import React from 'react';
import { Link } from 'react-router-dom';
import { theme } from '../../styles/theme';
import logo from '../../assets/images/bslogo.jpeg';

const Footer = () => {
  // Hover handlers for animated link underline + arrow
  const handleLinkEnter = (e) => {
    e.currentTarget.style.color = '#fff';
    const arrow = e.currentTarget.querySelector('.footer-arrow');
    if (arrow) {
      arrow.style.opacity = '1';
      arrow.style.transform = 'translateX(0)';
    }
  };
  const handleLinkLeave = (e) => {
    e.currentTarget.style.color = 'rgba(255,255,255,0.65)';
    const arrow = e.currentTarget.querySelector('.footer-arrow');
    if (arrow) {
      arrow.style.opacity = '0';
      arrow.style.transform = 'translateX(-6px)';
    }
  };

  const footerStyles = {
    position: 'relative',
    backgroundColor: '#0e0e0e',
    color: '#fff',
    padding: `calc(${theme.spacing.xxl} * 1.25) ${theme.spacing.lg} ${theme.spacing.xl}`,
    marginTop: 'auto',
    overflow: 'hidden',
  };

  const containerStyles = {
    maxWidth: '1280px',
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
  };

  const gridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing.xxl,
    paddingBottom: theme.spacing.xxl,
  };

  const brandColStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.lg,
    gridColumn: 'span 1',
  };

  const sectionStyles = {
    display: 'flex',
    flexDirection: 'column',
  };

  // Editorial small-cap tracked header with accent line — matches homepage
  const sectionHeader = (number, label) => (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
      marginBottom: theme.spacing.lg,
    }}>
      <span style={{
        width: '20px',
        height: '1px',
        backgroundColor: theme.colors.primary,
        display: 'inline-block',
      }} />
      <span style={{
        fontFamily: theme.typography.fontFamily.secondary,
        fontSize: theme.typography.fontSize.xs,
        fontWeight: theme.typography.fontWeight.bold,
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: theme.colors.primary,
      }}>
        {number} &nbsp;{label}
      </span>
    </div>
  );

  const linkStyles = {
    color: 'rgba(255,255,255,0.65)',
    textDecoration: 'none',
    fontFamily: theme.typography.fontFamily.secondary,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.normal,
    padding: `${theme.spacing.xs} 0`,
    transition: 'color 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    lineHeight: 1.5,
  };

  const linkArrowStyles = {
    opacity: 0,
    transform: 'translateX(-6px)',
    transition: 'opacity 0.3s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    fontSize: '0.85rem',
  };

  const contactRowStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginBottom: theme.spacing.md,
  };

  const contactLabelStyles = {
    fontSize: '0.7rem',
    fontFamily: theme.typography.fontFamily.secondary,
    fontWeight: theme.typography.fontWeight.bold,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.45)',
  };

  const contactValueStyles = {
    fontSize: theme.typography.fontSize.sm,
    fontFamily: theme.typography.fontFamily.secondary,
    color: '#fff',
    fontWeight: theme.typography.fontWeight.normal,
    margin: 0,
  };

  const dividerStyles = {
    borderTop: `1px solid rgba(255,255,255,0.08)`,
    paddingTop: theme.spacing.lg,
    paddingBottom: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
    flexWrap: 'wrap',
  };

  const copyrightTextStyles = {
    fontSize: theme.typography.fontSize.xs,
    color: 'rgba(255,255,255,0.5)',
    fontFamily: theme.typography.fontFamily.secondary,
    margin: 0,
    letterSpacing: '0.02em',
  };

  return (
    <footer style={footerStyles}>
      {/* Top accent gradient line */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: `linear-gradient(90deg, transparent 0%, ${theme.colors.primary} 30%, ${theme.colors.secondary} 70%, transparent 100%)`,
      }} />

      {/* Ambient glow */}
      <div style={{
        position: 'absolute',
        top: '-40%',
        right: '-20%',
        width: '60%',
        height: '120%',
        background: `radial-gradient(ellipse, ${theme.colors.primary}1F 0%, transparent 60%)`,
        filter: 'blur(80px)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-50%',
        left: '-20%',
        width: '60%',
        height: '120%',
        background: `radial-gradient(ellipse, ${theme.colors.secondary}14 0%, transparent 65%)`,
        filter: 'blur(80px)',
        pointerEvents: 'none',
      }} />

      {/* Subtle dot pattern */}
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.04,
        backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
        backgroundSize: '32px 32px',
        pointerEvents: 'none',
      }} />

      <div style={containerStyles}>
        <div style={gridStyles}>
          {/* Brand Column */}
          <div style={brandColStyles}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.md,
            }}>
              <img
                src={logo}
                alt="BS Trading Logo"
                style={{
                  height: '52px',
                  width: 'auto',
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
              <h3 style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize['2xl'],
                fontWeight: theme.typography.fontWeight.bold,
                color: '#fff',
                margin: 0,
                letterSpacing: '-0.01em',
              }}>
                BS Trading
              </h3>
            </div>
            <p style={{
              fontFamily: theme.typography.fontFamily.secondary,
              fontSize: theme.typography.fontSize.sm,
              lineHeight: 1.7,
              color: 'rgba(255,255,255,0.7)',
              maxWidth: '320px',
              margin: 0,
            }}>
              Your trusted source for quality home decor including cowhides, furniture, bags, onyx and more.
            </p>
          </div>

          {/* Company */}
          <div style={sectionStyles}>
            {sectionHeader('01', 'Company')}
            <Link to="/about" style={linkStyles} onMouseEnter={handleLinkEnter} onMouseLeave={handleLinkLeave}>
              <span className="footer-arrow" style={linkArrowStyles}>→</span>
              About
            </Link>
            <Link to="/contact" style={linkStyles} onMouseEnter={handleLinkEnter} onMouseLeave={handleLinkLeave}>
              <span className="footer-arrow" style={linkArrowStyles}>→</span>
              Contact
            </Link>
            <Link to="/wholesale" style={linkStyles} onMouseEnter={handleLinkEnter} onMouseLeave={handleLinkLeave}>
              <span className="footer-arrow" style={linkArrowStyles}>→</span>
              Wholesale Info
            </Link>
            <a
              href="https://bstradingco.faire.com?utm_source=embed"
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyles}
              onMouseEnter={handleLinkEnter}
              onMouseLeave={handleLinkLeave}
            >
              <span className="footer-arrow" style={linkArrowStyles}>→</span>
              Order on Faire
            </a>
          </div>

          {/* Customer Support */}
          <div style={sectionStyles}>
            {sectionHeader('02', 'Customer Support')}
            <div style={contactRowStyles}>
              <span style={contactLabelStyles}>Phone</span>
              <p style={contactValueStyles}>817-447-4433</p>
            </div>
            <div style={contactRowStyles}>
              <span style={contactLabelStyles}>Toll-Free</span>
              <p style={contactValueStyles}>1-800-554-0910</p>
            </div>
            <div style={contactRowStyles}>
              <span style={contactLabelStyles}>Email</span>
              <p style={contactValueStyles}>Orders@BSTrading.com</p>
            </div>
            <div style={contactRowStyles}>
              <span style={contactLabelStyles}>Hours</span>
              <p style={contactValueStyles}>Mon-Fri: 9am - 5pm</p>
            </div>
          </div>

          {/* Legal */}
          <div style={sectionStyles}>
            {sectionHeader('03', 'Legal')}
            <Link to="/terms" style={linkStyles} onMouseEnter={handleLinkEnter} onMouseLeave={handleLinkLeave}>
              <span className="footer-arrow" style={linkArrowStyles}>→</span>
              Terms
            </Link>
            <Link to="/privacy" style={linkStyles} onMouseEnter={handleLinkEnter} onMouseLeave={handleLinkLeave}>
              <span className="footer-arrow" style={linkArrowStyles}>→</span>
              Privacy
            </Link>
            <Link to="/shipping" style={linkStyles} onMouseEnter={handleLinkEnter} onMouseLeave={handleLinkLeave}>
              <span className="footer-arrow" style={linkArrowStyles}>→</span>
              Shipping Policy
            </Link>
            <Link to="/returns" style={linkStyles} onMouseEnter={handleLinkEnter} onMouseLeave={handleLinkLeave}>
              <span className="footer-arrow" style={linkArrowStyles}>→</span>
              Returns & Refunds
            </Link>
            <Link to="/product-disclaimer" style={linkStyles} onMouseEnter={handleLinkEnter} onMouseLeave={handleLinkLeave}>
              <span className="footer-arrow" style={linkArrowStyles}>→</span>
              Product Disclaimer
            </Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={dividerStyles}>
          <p style={copyrightTextStyles}>
            © {new Date().getFullYear()} BS Trading. All rights reserved.
          </p>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing.sm,
            fontSize: theme.typography.fontSize.xs,
            color: 'rgba(255,255,255,0.4)',
            fontFamily: theme.typography.fontFamily.secondary,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            fontWeight: theme.typography.fontWeight.bold,
          }}>
            <span style={{
              width: '24px',
              height: '1px',
              backgroundColor: 'rgba(255,255,255,0.25)',
            }} />
            BS Trading
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
